<script setup lang="ts">
import Modal from 'dashboard/components/Modal.vue';
import ModalHeader from 'dashboard/components/ModalHeader.vue';
import { ref } from 'vue';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  deleteType: 'schedule' | 'campaign';
  rowInfo: {
    campaignTitle: string;
    scheduledAt: string;
  };
  isLoading?: boolean;
}

const props = withDefaults(defineProps<DeleteConfirmationModalProps>(), {
  isLoading: false
});

const emit = defineEmits<{
  confirm: []
  cancel: []
}>();

const isProcessing = ref(false);

const handleConfirm = async () => {
  isProcessing.value = true;
  emit('confirm');
  // O componente pai deve resetar o isProcessing através da prop isLoading
};

const handleCancel = () => {
  if (isProcessing.value || props.isLoading) return;
  emit('cancel');
};
</script>

<template>
  <Modal
    v-if="isOpen"
    :show="isOpen"
    @close="handleCancel"
    class-name="max-w-md"
  >
    <ModalHeader
      :header-title="deleteType === 'schedule' ? '🗑️ Deletar Agendamento' : '🗑️ Deletar Campanha'"
    />

    <div class="p-6">
      <!-- Deletar Agendamento -->
      <div v-if="deleteType === 'schedule'" class="space-y-4">
        <div class="bg-n-amber-2 dark:bg-n-amber-3 rounded-lg p-4 border border-n-amber-6 dark:border-n-amber-7">
          <div class="flex items-start gap-3">
            <span class="i-lucide-calendar-x h-5 w-5 text-n-amber-11 mt-0.5"></span>
            <div>
              <h3 class="font-medium text-n-slate-12 mb-2">
                Deletar apenas este agendamento?
              </h3>
              <p class="text-sm text-n-slate-11 mb-3">
                Você está prestes a deletar o agendamento:
              </p>
              <div class="bg-n-slate-1 dark:bg-n-slate-2 rounded p-3 border border-n-amber-6 dark:border-n-amber-7">
                <div class="text-sm">
                  <div class="font-medium text-n-slate-12">{{ rowInfo.campaignTitle }}</div>
                  <div class="text-n-teal-11 flex items-center gap-1 mt-1">
                    <span class="i-lucide-calendar h-3 w-3"></span>
                    {{ rowInfo.scheduledAt }}
                  </div>
                </div>
              </div>
              <p class="text-xs text-n-slate-10 mt-3 flex items-center gap-1">
                <span class="i-lucide-info h-3 w-3"></span>
                A campanha e outros agendamentos permanecerão intactos.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Deletar Campanha -->
      <div v-else class="space-y-4">
        <div class="bg-n-ruby-2 dark:bg-n-ruby-3 rounded-lg p-4 border border-n-ruby-6 dark:border-n-ruby-7">
          <div class="flex items-start gap-3">
            <span class="i-lucide-alert-triangle h-5 w-5 text-n-ruby-11 mt-0.5"></span>
            <div>
              <h3 class="font-medium text-n-slate-12 mb-2">
                Deletar campanha e agendamentos pendentes?
              </h3>
              <p class="text-sm text-n-slate-11 mb-3">
                Você está prestes a deletar a campanha:
              </p>
              <div class="bg-n-slate-1 dark:bg-n-slate-2 rounded p-3 border border-n-ruby-6 dark:border-n-ruby-7">
                <div class="text-sm font-medium text-n-slate-12">
                  {{ rowInfo.campaignTitle }}
                </div>
              </div>
              <div class="bg-n-amber-2 dark:bg-n-amber-3 rounded p-3 mt-3 border border-n-amber-6 dark:border-n-amber-7">
                <p class="text-xs text-n-amber-11 flex items-start gap-2">
                  <span class="i-lucide-info h-3 w-3 mt-0.5 flex-shrink-0"></span>
                  <span>
                    Apenas agendamentos <strong>pendentes</strong> serão deletados. Agendamentos já executados, cancelados ou em execução serão mantidos no histórico.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Estado de Carregamento -->
      <div 
        v-if="isProcessing || isLoading" 
        class="flex items-center justify-center py-4"
      >
        <div class="flex items-center gap-3 text-n-slate-11">
          <div class="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
          <span class="text-sm">
            {{ deleteType === 'schedule' ? 'Deletando agendamento...' : 'Deletando campanha...' }}
          </span>
        </div>
      </div>

      <!-- Botões de ação -->
      <div class="flex gap-3 justify-end mt-6">
        <button
          @click="handleCancel"
          :disabled="isProcessing || isLoading"
          class="px-4 py-2.5 text-sm font-medium text-n-slate-11 bg-n-slate-1 dark:bg-n-slate-3 border border-n-slate-6 dark:border-n-slate-7 rounded-lg hover:bg-n-slate-2 dark:hover:bg-n-slate-4 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-n-slate-1 dark:disabled:hover:bg-n-slate-3"
        >
          Cancelar
        </button>
        <button
          @click="handleConfirm"
          :disabled="isProcessing || isLoading"
          :class="[
            'px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2',
            deleteType === 'schedule'
              ? 'bg-n-amber-9 hover:bg-n-amber-10 disabled:hover:bg-n-amber-9'
              : 'bg-n-ruby-9 hover:bg-n-ruby-10 disabled:hover:bg-n-ruby-9'
          ]"
        >
          <span 
            v-if="isProcessing || isLoading"
            class="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"
          ></span>
          {{ deleteType === 'schedule' ? 'Deletar Agendamento' : 'Deletar Campanha' }}
        </button>
      </div>
    </div>
  </Modal>
</template>